import React, { useState } from 'react';
import { View } from 'react-native';
import { useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from 'app/src/elements/buttons';
import { Fields, defaultValues, prepareInput } from 'app/src/elements/forms';
import { Title, Text, Warning, WarningText } from 'app/src/styles';
import { useNavigate } from 'app/src/utils/routing';

import { CREATE_OTHER_VENDOR_ORDER } from './queries';

const DEFAULT_SERIAL_PATTERN = /^Y[A-Z0-9]{2}\d{5}$/;
const DEFAULT_SERIAL_ERROR = 'errors.serialFormat.default';

const VENDORS = [
  {
    value: 'Boomerang',
    label: 'Boomerang Printing, LLC',
    serialPattern: /^YI0\d{5}$/,
    serialError:'errors.serialFormat.boomerang',
  },
  {
    value: 'Polytuff',
    label: 'POLYTUFF',
    serialPattern: /^YCM\d{5}$/,
    serialError:'errors.serialFormat.polytuff',
  },
  {
    value: 'Reynolds',
    label: 'Reynolds & Reynolds Company',
    serialPattern: /^YDR\d{5}$/,
    serialError:'errors.serialFormat.reynolds',
  },
  {
    value: 'Sooner',
    label: 'Sooner Temp Tags LLC',
    serialPattern: /^YAM\d{5}$/,
    serialError:'errors.serialFormat.sooner',
  },
];

export const FIELDS = {
  dealerId: {
    labelTranslation: 'labels.dealer',
    clientSelect: 'activeDealers',
    oneOption: 'no-choice',
    rules: { required: true },
  },
  vendor: {
    select: [{ value: '', label: 'Select' }, ...VENDORS],
    rules: { required: true },
  },
  productId: {
    labelTranslation: 'labels.product',
    hintTranslation: 'hints.product',
    clientSelect: 'activeProducts',
    rules: { required: true },
  },
  startSerial: {
    rules: { required: true }
  },
  quantity: {
    type: 'int',
    rules: { required: true }
  },
};

const OtherVendorOrder = () => {
  const [formStatus, setFormStatus] = useState();
  const [userErrors, setUserErrors] = useState();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    control, handleSubmit, setValue, watch, formState: { errors }
  } = useForm({
    defaultValues: { startSerial: '', quantity: '' }
  });

  const [createOtherVendorOrder] = useMutation(CREATE_OTHER_VENDOR_ORDER, {
    onCompleted: data => {
      setUserErrors(data.createOtherVendorOrder.errors);

      if (data.createOtherVendorOrder.order) {
        navigate('/orders');
      } else {
        setFormStatus('');
      }
    }
  });

  const onSubmit = input => {
    setFormStatus('submitting');
    createOtherVendorOrder({ variables: prepareInput(input, FIELDS) });
  };

  const vendor = VENDORS.find(entry => watch('vendor') === entry.value);

  const fields = { ...FIELDS };
  fields.startSerial = {
    ...fields.startSerial,
    rules: {
      required: true,
      pattern: {
        value: vendor?.serialPattern || DEFAULT_SERIAL_PATTERN,
        message: t(vendor?.serialError || DEFAULT_SERIAL_ERROR),
      },
    }
  };

  return (
    <View>
      <Title>{t('titles.otherVendorOrder')}</Title>

      <Text style={{ marginBottom: 12 }}>{t('notices.otherVendorOrder')}</Text>

      <Fields
        fields={fields}
        control={control}
        errors={errors}
        setValue={setValue}
      />

      {userErrors && userErrors.length > 0 && (
        <Warning>
          {userErrors.map(error => (
            <WarningText style={{ fontSize: 20 }}>{error.message}</WarningText>
          ))}
        </Warning>
      )}

      <Button
        label={t('actions.save')}
        onPress={handleSubmit(onSubmit)}
        disabled={'submitting' === formStatus}
        wide
      />
    </View>
  );
};

export default OtherVendorOrder;
